import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Empty, Input, Pagination, Select, Spin, Tag, message } from "antd";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";
import ReadOnlyDocumentViewer from "../../components/Document/ReadOnlyDocumentViewer";
import {
  fetchArticleBankEntries,
  fetchArticleBankEntry,
  fetchArticleBankFacets,
} from "../../service/articleBank";
import {
  DetailHeading,
  DetailInfo,
  DetailLayout,
  DetailSection,
  DetailValue,
  EmptyState,
  FilterFooter,
  FilterLabel,
  Filters,
  Header,
  InlinePreviewPanel,
  Meta,
  Page,
  PaginationRow,
  PreviewBody,
  ResultCard,
  ResultList,
  ResultSummary,
  ResultTitle,
  ResultTop,
  SearchPanel,
  SearchRow,
  Snippet,
  Subtitle,
  TagRow,
  Title,
} from "../BriefBank/BriefBankStyled";
import {
  Quote,
  QuotePage,
  SupportExplanation,
  SupportItem,
  SupportLabel,
} from "./ArticlesBankStyled";

const PAGE_SIZE = 25;
const STRENGTH_COLORS = { strong: "green", moderate: "gold", weak: "default" };
const VISIBLE_CONDITIONS = 5;

const responseData = (response) => response?.data || response || {};

const apiError = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.detail ||
  error?.message ||
  fallback;

const highlightedSnippet = (snippet) => {
  const parts = String(snippet || "").split(/(\[\[HIT\]\]|\[\[\/HIT\]\])/);
  let highlighted = false;
  return parts.map((part, index) => {
    if (part === "[[HIT]]") {
      highlighted = true;
      return null;
    }
    if (part === "[[/HIT]]") {
      highlighted = false;
      return null;
    }
    return highlighted ? <mark key={`${part}-${index}`}>{part}</mark> : part;
  });
};

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const facetOptions = (items = []) =>
  items.map((item) => ({ value: item.value, label: `${item.label} (${item.count})` }));

const ArticlesBank = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const conditions = searchParams.getAll("condition");
  const elements = searchParams.getAll("element");
  const kinds = searchParams.getAll("kind");
  const page = Number(searchParams.get("page") || 1);
  const paramsKey = searchParams.toString();

  const [queryInput, setQueryInput] = useState(query);
  const [facets, setFacets] = useState({});
  const [facetLoading, setFacetLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchError, setSearchError] = useState("");
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailRequestRef = useRef(0);

  useEffect(() => setQueryInput(query), [query]);

  useEffect(() => {
    let cancelled = false;
    fetchArticleBankFacets()
      .then((response) => {
        if (!cancelled) setFacets(responseData(response));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setFacetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSearchError("");
    fetchArticleBankEntries({ page, pageSize: PAGE_SIZE, query, conditions, elements, kinds })
      .then((response) => {
        if (cancelled) return;
        const data = responseData(response);
        setEntries(data.results || []);
        setTotal(data.count || 0);
      })
      .catch((error) => {
        if (cancelled) return;
        setEntries([]);
        setTotal(0);
        setSearchError(apiError(error, "Articles could not load."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // paramsKey stands in for every URL-backed filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const updateParams = (mutate) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    next.set("page", "1");
    setSearchParams(next);
  };

  const setMany = (key, values) =>
    updateParams((next) => {
      next.delete(key);
      values.forEach((value) => next.append(key, value));
    });

  const openDetail = async (entry) => {
    const requestId = ++detailRequestRef.current;
    setSelected(entry);
    setDetailLoading(true);
    try {
      const response = await fetchArticleBankEntry(entry.id);
      if (requestId === detailRequestRef.current) setSelected(responseData(response));
    } catch (error) {
      if (requestId === detailRequestRef.current) {
        message.error(apiError(error, "Article details could not load."));
      }
    } finally {
      if (requestId === detailRequestRef.current) setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    detailRequestRef.current += 1;
    setSelected(null);
    setDetailLoading(false);
  };

  const conditionOptions = useMemo(() => facetOptions(facets.conditions), [facets]);
  const elementOptions = useMemo(() => facetOptions(facets.elements), [facets]);
  const kindOptions = useMemo(() => facetOptions(facets.kinds), [facets]);
  const canPreview = Boolean(selected?.source_document_id && selected?.claim_support);
  const sourceType = String(selected?.source_name || "").split(".").pop().toLowerCase();

  return (
    <Page>
      <Header>
        <div>
          <Title>Articles Bank</Title>
          <Subtitle>
            Medical and legal research the firm uses to support claims. Search by
            condition and by the part of the claim an article helps prove.
          </Subtitle>
        </div>
      </Header>

      <SearchPanel>
        <SearchRow
          onSubmit={(event) => {
            event.preventDefault();
            updateParams((next) => {
              if (queryInput.trim()) next.set("q", queryInput.trim());
              else next.delete("q");
            });
          }}
        >
          <Input
            size="large"
            prefix={<MagnifyingGlass size={18} />}
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
            placeholder='Search article text, such as "burn pits"'
            allowClear
          />
          <Button type="primary" size="large" htmlType="submit">
            Search
          </Button>
        </SearchRow>

        <Filters>
          <FilterLabel>
            Condition
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              loading={facetLoading}
              value={conditions}
              placeholder="Any condition"
              options={conditionOptions}
              onChange={(values) => setMany("condition", values)}
            />
          </FilterLabel>
          <FilterLabel>
            Part of the claim it supports
            <Select
              mode="multiple"
              loading={facetLoading}
              value={elements}
              placeholder="Any part of the claim"
              options={elementOptions}
              onChange={(values) => setMany("element", values)}
            />
          </FilterLabel>
          <FilterLabel>
            Kind of article
            <Select
              mode="multiple"
              loading={facetLoading}
              value={kinds}
              placeholder="Any kind"
              options={kindOptions}
              onChange={(values) => setMany("kind", values)}
            />
          </FilterLabel>
        </Filters>
        <FilterFooter>
          <span style={{ color: "#66758a", fontSize: 12 }}>
            Choosing a condition and a part of the claim finds articles that support
            that condition for that part, such as sleep apnea as a secondary condition.
          </span>
          <Button
            type="link"
            onClick={() => {
              setQueryInput("");
              setSearchParams(new URLSearchParams({ page: "1" }));
            }}
          >
            Clear filters
          </Button>
        </FilterFooter>
      </SearchPanel>

      {searchError && (
        <div role="alert" style={{ marginTop: 14, color: "#b42318" }}>
          {searchError}
        </div>
      )}

      <ResultSummary>
        {loading ? "Searching…" : `${total.toLocaleString()} articles found`}
      </ResultSummary>

      {loading ? (
        <EmptyState style={{ textAlign: "center" }}>
          <Spin />
        </EmptyState>
      ) : entries.length === 0 ? (
        <EmptyState>
          <Empty description="No articles match these filters." />
        </EmptyState>
      ) : (
        <ResultList>
          {entries.map((entry) => (
            <ResultCard
              key={entry.id}
              role="button"
              tabIndex={0}
              onClick={() => openDetail(entry)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openDetail(entry);
                }
              }}
            >
              <ResultTop>
                <div>
                  <ResultTitle>{entry.title}</ResultTitle>
                  <Meta>
                    <span>{entry.article_kind_label}</span>
                    {entry.year && <span>{entry.year}</span>}
                    {entry.publication && <span>{entry.publication}</span>}
                  </Meta>
                </div>
                {entry.strength && (
                  <Tag color={STRENGTH_COLORS[entry.strength] || "default"}>
                    {capitalize(entry.strength)}
                  </Tag>
                )}
              </ResultTop>
              {entry.snippet ? (
                <Snippet>{highlightedSnippet(entry.snippet)}</Snippet>
              ) : (
                entry.summary && <Snippet>{entry.summary}</Snippet>
              )}
              <TagRow>
                {(entry.claim_elements || []).map((element) => (
                  <Tag color="purple" key={element.value}>
                    {element.label}
                  </Tag>
                ))}
                {(entry.conditions || []).slice(0, VISIBLE_CONDITIONS).map((condition) => (
                  <Tag color="blue" key={condition}>
                    {condition}
                  </Tag>
                ))}
                {(entry.conditions || []).length > VISIBLE_CONDITIONS && (
                  <Tag>+{entry.conditions.length - VISIBLE_CONDITIONS}</Tag>
                )}
              </TagRow>
            </ResultCard>
          ))}
        </ResultList>
      )}

      {!loading && total > PAGE_SIZE && (
        <PaginationRow>
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            showSizeChanger={false}
            onChange={(nextPage) => {
              const next = new URLSearchParams(searchParams);
              next.set("page", String(nextPage));
              setSearchParams(next);
            }}
          />
        </PaginationRow>
      )}

      <Drawer
        title={selected?.title || "Article"}
        width={canPreview ? "min(1500px, calc(100vw - 24px))" : 520}
        open={Boolean(selected)}
        onClose={closeDetail}
      >
        {detailLoading ? (
          <Spin />
        ) : (
          selected && (
            <DetailLayout $withPreview={canPreview}>
              <DetailInfo>
                <DetailSection>
                  <DetailHeading>Article</DetailHeading>
                  <DetailValue>
                    {[selected.authors, selected.publication, selected.year]
                      .filter(Boolean)
                      .join(" · ")}
                    {selected.summary && (
                      <p style={{ margin: "8px 0 0" }}>{selected.summary}</p>
                    )}
                  </DetailValue>
                </DetailSection>
                <DetailSection>
                  <DetailHeading>How it helps a claim</DetailHeading>
                  {(selected.claim_support || []).length === 0 && (
                    <DetailValue>No specific claim support recorded.</DetailValue>
                  )}
                  {(selected.claim_support || []).map((item, index) => (
                    <SupportItem key={`${item.element}-${index}`}>
                      <SupportLabel>{item.element_label}</SupportLabel>
                      {(item.conditions || []).length > 0 && (
                        <TagRow style={{ marginTop: 6 }}>
                          {item.conditions.map((condition) => (
                            <Tag color="blue" key={condition}>
                              {condition}
                            </Tag>
                          ))}
                        </TagRow>
                      )}
                      {item.explanation && (
                        <SupportExplanation>{item.explanation}</SupportExplanation>
                      )}
                      {item.quote && (
                        <Quote>
                          “{item.quote}”
                          {item.page && <QuotePage>p. {item.page}</QuotePage>}
                        </Quote>
                      )}
                    </SupportItem>
                  ))}
                </DetailSection>
                {(selected.exposures_events || []).length > 0 && (
                  <DetailSection>
                    <DetailHeading>Exposures and events</DetailHeading>
                    <TagRow>
                      {selected.exposures_events.map((value) => (
                        <Tag key={value}>{value}</Tag>
                      ))}
                    </TagRow>
                  </DetailSection>
                )}
                <DetailSection>
                  <DetailHeading>File</DetailHeading>
                  <DetailValue>{selected.source_name}</DetailValue>
                </DetailSection>
              </DetailInfo>
              {canPreview && (
                <InlinePreviewPanel>
                  <PreviewBody $embedded>
                    <ReadOnlyDocumentViewer
                      key={selected.source_document_id}
                      documentId={selected.source_document_id}
                      filename={selected.source_name}
                      fileType={sourceType}
                    />
                  </PreviewBody>
                </InlinePreviewPanel>
              )}
            </DetailLayout>
          )
        )}
      </Drawer>
    </Page>
  );
};

export default ArticlesBank;
